import {NO_VOTE} from "../utils/votes";

//
// Profile
//

export const selectUsername = ({settings}) => settings.username;

export const selectLoggedIn = ({settings}) => Boolean(settings.username);

export const selectProfile = ({settings}) => settings;

//
// Communities
//

export const selectCommunityListEntry = (sort) => ({communityLists}) =>
    communityLists.get(sort);

export const selectCommunities = (names) => ({communityMap}) =>
    names?.map((name) => name && communityMap.get(name.toLowerCase()));

/** selectStaleCommunities builds a selector that returns the names of
 *  communities that are either not loaded, or have a timestamp older than ref.
 * @param names {string[]}
 * @param ref {number}
 * @returns {function({communityMap: *}): *[]}
 */
export const selectStaleCommunities = (names, ref) => ({communityMap}) => {
    const result = [];
    for (const name of names) {
        const community = communityMap.get(name.toLowerCase());
        if (!community || community.timestamp < ref) {
            result.push(name);
        }
    }
    return result;
}

//
// Comments
//

export const selectCommentsPage = (key) => ({ commentLists }) => {
    return commentLists.get(key)?.page || 0;
};

export const selectCommentList = (key) => ({ commentLists, commentMap }) => {
    const {ids, page, more, loading} = commentLists.get(key) || { ids: [], page: 0, more: false, loading: false };
    const comments = ids.map((id) => id && commentMap.get(id));
    return {comments, page, more: more && !loading, loading: !ids || loading};
};

export const selectCommentThread = (key) => ({ commentLists, commentMap }) => {
    const {ids} = commentLists.get(key) || { ids: [] };
    return ids?.length ? commentMap.get(ids[0]) : null;
};

export const selectComment = (id) => ({commentMap}) => commentMap.get(id);

export const selectCommentsFlat = (key) => ({commentLists, commentMap}) => {
    const items = [];
    function mark(index) {
        if (items.length > 0) {
            items[index].span = items.length - index;
        }
    }
    function collect(id, parent, depth) {
        const key = `c/${id}`;
        const index = items.length;
        const comment = commentMap.get(id);
        items.push({ key, parent: parent - index, depth, comment });
        const childIds = commentLists.get(key)?.ids || [];
        for (let childIndex = 0; childIndex < childIds.length; childIndex++) {
            collect(childIds[childIndex], index, depth + 1);
        }
        mark(index);
    }
    const ids = commentLists.get(key)?.ids || [];
    for (let index = 0; index < ids.length; index++) {
        collect(ids[index], -1, 0);
    }
    return items;
};

export const selectCommentVoteState = (id) => ({commentMap}) => {
    const comment = commentMap.get(id);
    return (!comment || !('vote_state' in comment)) ? NO_VOTE : comment.vote_state;
};

//
// Posts
//

export const selectPost = (id, withComments=false) => (state) => {
    const post = state.postMap.get(id);
    if (!withComments) {
        return { post };
    }
    const commentsKey = `p/${id}`;
    const { more, comments } = selectCommentList(commentsKey)(state);
    return { post, comments, hasMoreComments: more };
};

export const selectPosts = (ids, withHidden=false, withDeleted=false) => ({postMap}) => {
    const posts = ids.map((id) => postMap.get(id));
    return posts.filter((post) => {
        return post && (!post.hidden || withHidden) && (!post.is_deleted || withDeleted);
    });
};

export const selectPostVoteState = (id) => ({postMap}) => {
    const post = postMap.get(id);
    return (!post || !('vote_state' in post)) ? NO_VOTE : post.vote_state;
};

//
// Feeds
//

export const selectFeedEntry = (key) => ({postLists}) => {
    return postLists.get(key);
};

export const selectFeedIsLoaded = (key) => ({postLists}) => {
    return Boolean(postLists.get(key));
};

export const selectFeedLastPost = (key) => ({postLists, postMap}) => {
    const feed = postLists && postLists.get(key);
    if (feed) {
        const {ids} = feed;
        const lastPostId = (ids && ids.length > 0) && ids[ids.length - 1];
        return lastPostId ? postMap.get(lastPostId) : null;
    }
    return null;
};

export const selectSavedFeed = (id) => (state) => {
    if (state?.savedFeeds) {
        for (const feed of state.savedFeeds) {
            if (feed.id === id) return feed;
        }
    }
    return false;
};

//
// Content
//

export const selectContentList = (key) => ({contentLists}) => contentLists.get(key);

export const selectContent = (items) => ({postMap, commentMap}) =>
    items.map(
        (item) => item && (item.type === 'post' ? postMap.get(item.id) : commentMap.get(item.id))
    );